// exports Matrix, Matrix22, default: Matrix

import { isFinite, map } from 'lodash';

import { Base, deg2rad, rad2deg } from 'gear';

import { Point } from './point';

import { Xml } from '../util';

const _atan2 = Math.atan2, _abs = Math.abs ;

export const Matrix = function(matrix) {
  if( ! ( this instanceof Matrix )) {
    return new Matrix(matrix);
  }
  const _this = this;
  if(matrix) {
    _this.s00 = matrix.s00;
    _this.s01 = matrix.s01;
    _this.dx  = matrix.dx || 0;
    _this.s10 = matrix.s10;
    _this.s11 = matrix.s11;
    _this.dy  = matrix.dy || 0;
  } else { //identity
    _this.s00 = 1;
    _this.s01 = 0;
    _this.dx  = 0;
    _this.s10 = 0;
    _this.s11 = 1;
    _this.dy  = 0;
  }
};

Base.Type['Matrix']   = Matrix;
Base.Type['Matrix22'] = Matrix;

export const Matrix22 = Matrix;
export default Matrix;

function numDef( value, def ) {
  return isFinite(value) ? value : def ;
}

Matrix.stringToMatrix22 = function( string ) {
  const mv = map(string.split(','),parseFloat);
  const s00 = numDef(mv[0],1), s01 = numDef(mv[1],0), s10 = numDef(mv[2],0), s11= numDef(mv[3],1);
  const det = s00 * s11 - s01 * s10;

  if(det != 0)
    return new Matrix({ s00: s00, s01: s01, s10: s10, s11: s11, dx: 0, dy: 0 });
  else
    return new Matrix({ s00: 1, s01: 0, s10: 0, s11: 1, dx: 0, dy: 0 })
};

function multiply( l, r ) {
  const l_s00 = l.s00, l_s01 = l.s01, l_s10 = l.s10, l_s11 = l.s11;
  return new Matrix({
    s00: l_s00 * r.s00 + l_s01 * r.s10 ,
    s01: l_s00 * r.s01 + l_s01 * r.s11 ,
    dx : l_s00 * r.dx  + l_s01 * r.dy  + l.dx ,
    s10: l_s10 * r.s00 + l_s11 * r.s10 ,
    s11: l_s10 * r.s01 + l_s11 * r.s11 ,
    dy : l_s10 * r.dx  + l_s11 * r.dy  + l.dy
  });
}
Matrix.multiply = Matrix.m = multiply;

Matrix.concat = function() {
  // g.assert(arguments.length >= 2,'Matrix.concat');
  let result = arguments[0];
  for( let i = 1; i < arguments.length; ++i) {
    result = multiply(result, arguments[i]);
  }
  return result;
};

function _appended( m ) {
  return multiply(this,m);
}

function _scaleFactor() {
  return Math.sqrt(_abs(this.determinant()));
}

function _isNear( other, eps = 0.0001 ) {
  const _this = this;
  return _abs( _this.s00-other.s00 ) < eps &&
         _abs( _this.s01-other.s01 ) < eps &&
         _abs( _this.s10-other.s10 ) < eps &&
         _abs( _this.s11-other.s11 ) < eps &&
         _abs( _this.dx -other.dx  ) < eps &&
         _abs( _this.dy -other.dy  ) < eps ;
}

function translate( dx, dy ) { if( dy === undefined ) { const p = dx; dx = p.x; dy = p.y; }
  return new Matrix({ s00: 1, s01: 0, s10: 0, s11: 1, dx: dx, dy: dy });
}
Matrix.translate = translate;

function rotate_rad( phi ) {
  if( phi !== 0 ) {
    const cos = Math.cos(phi);
    const sin = Math.sin(phi);
    return new Matrix({ s00: cos, s01: -sin, s10: sin, s11: cos, dx: 0, dy: 0 });
  }
  else {
    return new Matrix();
  }
}

function rotate( deg ) {
  return rotate_rad(deg2rad(deg));
}
Matrix.rotate = rotate;

function transformAround( point, matrix ) {
  const x = point.x, y = point.y;
  return multiply( translate(x,y), multiply( matrix, translate(-x,-y) ) );
}
Matrix.transformAround = transformAround;

function transformRotated( deg, matrix ) {
  const phi = deg2rad(deg);
  return multiply( rotate_rad(phi), multiply( matrix, rotate_rad(-phi) ) );
}

function scale( sx, sy, deg ) {
  if( sy === undefined ) { sy=sx; }
  if( deg === undefined ) {
    return new Matrix({ s00: sx, s01: 0, s10: 0, s11: sy, dx: 0, dy: 0 });
  }
  else {
    return transformRotated( deg, scale(sx,sy) );
  }
}
Matrix.scale = scale;

function scaleFrom( point, sx, sy, deg ) {
  return transformAround(point, scale(sx, sy, deg) );
}
Matrix.scaleFrom = scaleFrom;

function rotateAround( point, deg ) {
  return transformAround(point, rotate(deg) );
}
Matrix.rotateAround = rotateAround;

function shear( sx, sy, deg ) {
  if( deg === undefined ) {
    return new Matrix({ s00: 1.0, s01: sx, s10: sy, s11: 1.0, dx: 0, dy: 0 });
  }
  else {
    return transformRotated( deg, shear(sx,sy) );
  }
}
Matrix.shear = shear;

function shearAround( point, sx, sy, deg ) {
  return transformAround(point, shear(sx, sy, deg) );
}
Matrix.shearAround = shearAround;

function invert( m ) {
  const m_s00 = m.s00, m_s01 = m.s01, m_s10 = m.s10, m_s11 = m.s11;
  const det = m_s00 * m_s11 - m_s01 * m_s10;
  return det === 0.0 ? null : new Matrix({
    s00:  m_s11 / det,
    s01: -m_s01 / det,
    s10: -m_s10 / det,
    s11:  m_s00 / det,
    dx: ( m_s01 * m.dy - m.dx * m_s11 ) / det,
    dy: ( m_s10 * m.dx - m.dy * m_s00 ) / det
  });
}
Matrix.invert = invert;

function _toFixed(x) {
  return (+x.toFixed(4));
}

function _textualize() {
  const _this = this, sep = ',';
  return _toFixed(_this.s00) + sep +
         _toFixed(_this.s01) + sep +
         _toFixed(_this.s10) + sep +
         _toFixed(_this.s11) + sep +
         _toFixed(_this.dx ) + sep +
         _toFixed(_this.dy );
}

Matrix.prototype = {
  constructor: Matrix,
  type: 'Matrix',

  get matrix() {
    return this;
  },

  get matrix22() {
    const m = this.clone();
    m.dx = 0; m.dy = 0;
    return m;
  },

  set( m ) {
    const _this = this;
    _this.s00 = m.s00;
    _this.s01 = m.s01;
    _this.s10 = m.s10;
    _this.s11 = m.s11;
    _this.dx  = m.dx;
    _this.dy  = m.dy;
    return this;
  },

  apply( point ) {
    const _this = this;
    return new Point( point.x * _this.s00 + point.y * _this.s01 + _this.dx ,
                      point.x * _this.s10 + point.y * _this.s11 + _this.dy );
  },
  apply_( x, y ) {
    const _this = this;
    return new Point( x * _this.s00 + y * _this.s01 + _this.dx ,
                      x * _this.s10 + y * _this.s11 + _this.dy );
  },

  applyInplace( p ) {  // Do not reuse other functions for performance
    const x = p.x, y = p.y, _this = this;
    p.x = x * _this.s00 + y * _this.s01 + _this.dx;
    p.y = x * _this.s10 + y * _this.s11 + _this.dy;
  },
  isIdentity() {
    const _this = this;
    return _this.s00 === 1 &&
           _this.s01 === 0 &&
           _this.s10 === 0 &&
           _this.s11 === 1 &&
           _this.dx  === 0 &&
           _this.dy  === 0 ;
  },

  appended : _appended,
  m: _appended,

  determinant() {
    return this.s00 * this.s11 - this.s01 * this.s10 ;
  },
  getScaleFactor : _scaleFactor,
  scaleFactor : _scaleFactor,
  maxScaleFactor() {
    // This is not exact
    const sf = this._scaleFactors();
    return Math.sqrt( Math.max(sf[0],sf[1]) );
  },
  _scaleFactors() {
    const r = this.angle_rad();
    const m = multiply( this, rotate_rad(-r) );
    m.dx = m.dy = 0;
    const sX2 = m.apply_(1,0).norm2();
    const sY2 = m.apply_(0,1).norm2();
    return [ sX2, sY2 ];
  },
  angle_rad() {
    return _atan2(this.s10, this.s00);
  },
  angle() {
    return rad2deg( _atan2(this.s10, this.s00) ) ;
  },
  isAlmostIsotropic() {
    const sf = this._scaleFactors();
    return _abs( 1 - sf[0] / sf[1] ) < 0.01 ;
  },
  isNear: _isNear,
  isEqual: _isNear,
  toCssString( unit) {
    const _this = this;
    return 'matrix(' + _this.s00 + ',' + _this.s01 + ',' + _this.s10 + ',' + _this.s11 + ',' + _this.dx + unit + ',' + (-_this.dy) + unit + ')';
  },
  clone() {
    return new Matrix(this);
  },
  invert() {
    return this.set(this.inverted());
  },
  inverted() {
    return invert(this);
  },
  toMatrix22() {
    return new Matrix({ s00: this.s00, s01: this.s01, s10: this.s10, s11: this.s11 });
  },
  toMatrix22String() {
    const sep = ',';
    return _toFixed(this.s00) + sep +
           _toFixed(this.s01) + sep +
           _toFixed(this.s10) + sep +
           _toFixed(this.s11);
  },

  transformContext( context ) {
    const t = this;
    context.transform(t.s00,t.s10,t.s01,t.s11,t.dx,t.dy);
  },

  toMatrixString: _textualize,
  textualize: _textualize,

  setMatrix22Attr( node, tag ) {
    node.setAttribute(tag,this.toMatrix22String());
  }
};

function add( m1, m2 ) {
  return new Matrix({ s00: m1.s00+m2.s00, s01: m1.s01+m2.s01 ,
                      s10: m1.s10+m2.s10, s11: m1.s11+m2.s11 ,
                      dx : m1.dx +m2.dx , dy : m1.dy +m2.dy  });
}
Matrix.add = add;

function substract( m1, m2 ) {
  return new Matrix({ s00: m1.s00-m2.s00, s01: m1.s01-m2.s01 ,
                      s10: m1.s10-m2.s10, s11: m1.s11-m2.s11 ,
                      dx : m1.dx -m2.dx , dy : m1.dy -m2.dy  });
}
Matrix.substract = substract;

function scalarMultiply( m, a ) {
  return new Matrix({ s00: m.s00*a, s01: m.s01*a ,
                      s10: m.s10*a, s11: m.s11*a ,
                      dx : m.dx *a, dy : m.dy *a });
}
Matrix.sm = Matrix.scalarMultiply = scalarMultiply;


Matrix.getMatrix22Attr = function( node, tag, def ) {
  const valueString = node.getAttribute(tag) ;
  if( valueString ) {
    return Matrix.stringToMatrix22(valueString);
  }
  return def === undefined ? new Matrix() : def;
};

Xml.Parser['Matrix22'] = {
  decode( node, tag, v ) {
    return Matrix.getMatrix22Attr(node,tag,v);
  },
  encode( node, tag, v ) {
    v.setMatrix22Attr(node,tag);
  }
};



/** WEBPACK FOOTER **
 ** ./src/cdl/geometry/matrix.js
 **/